import { CoreSegmentCell } from "pro-features/components/core cells/src/CoreSegmentCell/CoreSegmentCell";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { useMemo } from "react";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { DefaultCellRightAlign } from "../../../../components/React/Table/cells/DefaultCellRightAlign";
import { BounceRate, IndexCell, TrafficShare } from "../../../../components/React/Table/cells";
import { RowSelectionConsumer } from "../../../../components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "../../../../components/React/Table/headerCells";
import { translate } from "../../../../filters/i18nFilter";
import { allTrackers } from "../../../../services/track/track";
import { SegmentCellContainer } from "./StyledComponents";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { WinnerCellWrapper } from "app/components/React/Table/cells/WinnerCellWrapper";
import { SwTrack } from "app/services/SwTrack";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const wrapCellComponentWithWinner = (CellComponent: any) => (props: any) => {
    const { row, field, tableColumns } = props;
    const columnConfig = tableColumns.find((c) => c.field === field);
    const isWinner = useMemo(() => {
        return columnConfig.showWinner ? (row?.winnersList || []).includes(field) : false;
    }, [field, row.winnersList]);

    return (
        <WinnerCellWrapper
            {...props}
            isWinner={isWinner}
            cellComponent={CellComponent}
            winnerCellAlign="right"
            winnerCellBold={true}
        />
    );
};

export const wrapCellComponentDisabled = (CellComponent: any) => (props: any) => {
    const { row } = props;
    if (row.isDisabled) {
        return null;
    }
    return <CellComponent {...props} />;
};

const SegmentsGroupsAnalysisColumnsConfig = () => [
    {
        fixed: true,
        cellComponent: RowSelectionConsumer,
        sortable: false,
        width: 33,
        headerComponent: DefaultCellHeader,
        visible: true,
        disableHeaderCellHover: true,
    },
    {
        fixed: true,
        cellComponent: IndexCell,
        headerComponent: DefaultCellHeader,
        sortable: false,
        disableHeaderCellHover: true,
        width: 65,
        visible: true,
    },
    {
        fixed: true,
        field: "SegmentDomain",
        displayName: translate("segments.group.analysis.table.column.segments"),
        type: "string",
        format: "None",
        sortable: true,
        isSorted: false,
        cellComponent: ({ value, row }) => {
            const subtitleFilters = [];
            const editSegmentClick = () => {
                SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${row.SegmentId}`);
                const currentModule = proNavigator.getCurrentModule();
                const segmentWizardStateName = `${currentModule}-wizard`;
                proNavigator.go(segmentWizardStateName, { sid: row.SegmentId });
            };
            if (!row.SegmentIsWebsite) {
                subtitleFilters.push({
                    filter: "text",
                    value: row.SegmentName,
                });
            }
            return (
                <SegmentCellContainer withSegment={row.SegmentType === SEGMENT_TYPES.SEGMENT}>
                    {row.SegmentType === SEGMENT_TYPES.WEBSITE ? (
                        <CoreWebsiteCell
                            withWebsiteSign={true}
                            icon={row.SegmentFavicon}
                            domain={value}
                            domainBadge="WEBSITE"
                            externalLink={`http://${value}`}
                            subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                            trackExternalLink={() =>
                                allTrackers.trackEvent("external link", "click", "Segments Group Analysis")
                            }
                            hideTrackButton={true}
                        />
                    ) : (
                        <CoreSegmentCell
                            withWebsiteSign={true}
                            segmentName={row.SegmentName}
                            lastModified={row.SegmentLastUpdated}
                            isOrgSegment={!itsMyOwnSegment(row.userId)}
                            onClick={editSegmentClick}
                            segmentId={row.SegmentId}
                            icon={row.SegmentFavicon}
                            domain={value}
                            externalLink={`http://${value}`}
                            subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                            trackExternalLink={() =>
                                allTrackers.trackEvent("external link", "click", "Segments Group Analysis")
                            }
                        />
                    )}
                </SegmentCellContainer>
            );
        },
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        totalCount: true,
        tooltip: translate("segments.group.analysis.table.tooltip.segments"),
        width: 300,
        showTotalCount: false,
        visible: true,
    },
    {
        field: "GroupVisitsShare",
        displayName: translate("segments.group.analysis.table.column.groupVisitsShare"),
        type: "double",
        format: "percentagesign",
        sortable: true,
        isSorted: false,
        cellComponent: wrapCellComponentDisabled(TrafficShare),
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.groupVisitsShare"),
        minWidth: 230,
        visible: true,
    },
    {
        field: "Visits",
        displayName: translate("segments.group.analysis.table.column.visits"),
        type: "double",
        format: "minVisitsAbbr",
        sortable: true,
        isSorted: true,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(DefaultCellRightAlign)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.visits"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
    {
        field: "TrafficShare",
        displayName: translate("segments.group.analysis.table.column.trafficShare"),
        type: "double",
        format: "percentagesign",
        sortable: true,
        isSorted: true,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(BounceRate)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.trafficShare"),
        minWidth: 120,
        visible: true,
        showWinner: false,
    },
    {
        field: "UniqueUsers",
        displayName: translate("segments.group.analysis.table.column.UniqueUsers"),
        type: "double",
        format: "minVisitsAbbr",
        sortable: true,
        isSorted: true,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(DefaultCellRightAlign)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.UniqueUsers"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
    {
        field: "PagesViews",
        displayName: translate("segments.group.analysis.table.column.pageviews"),
        type: "double",
        format: "minVisitsAbbr",
        sortable: true,
        isSorted: false,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(DefaultCellRightAlign)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.pageviews"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
    {
        field: "PagePerVisit",
        displayName: translate("segments.group.analysis.table.column.pagePerVisit"),
        type: "double",
        format: "number",
        sortable: true,
        isSorted: false,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(DefaultCellRightAlign)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.pagePerVisit"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
    {
        field: "Duration",
        displayName: translate("segments.group.analysis.table.column.visitDuration"),
        type: "double",
        format: "time",
        sortable: true,
        isSorted: false,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(DefaultCellRightAlign)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        tooltip: translate("segments.group.analysis.table.tooltip.visitDuration"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
    {
        field: "BounceRate",
        displayName: translate("segments.group.analysis.table.column.bounceRate"),
        type: "double",
        format: "percentagesign",
        sortable: true,
        isSorted: false,
        cellComponent: wrapCellComponentDisabled(wrapCellComponentWithWinner(BounceRate)),
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "asc",
        tooltip: translate("segments.group.analysis.table.tooltip.bounceRate"),
        minWidth: 120,
        visible: true,
        showWinner: true,
    },
];

export const SegmentsGroupsAnalysisColumnsConfigGen = (sortbyField: string, sortDirection: string) => {
    return SegmentsGroupsAnalysisColumnsConfig().map((col) => {
        if (!col.sortable) {
            return col;
        }
        return {
            ...col,
            isSorted: col.field === sortbyField,
            sortDirection: col.field === sortbyField ? sortDirection : col.sortDirection,
        };
    });
};
